import { useState, useEffect } from 'react';
import { isAxiosError } from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import './AchievementsTeamsSegment.css';

export interface IAchievement {
  idOsiagniecia: number;
  nazwa: string;
}

interface IAchievementLink {
  idOsiagniecia: number;
}

interface AchievementsTeamsSegmentProps {
  bandId?: number | null;
}

const AchievementsTeamsSegment = ({ bandId = null }: AchievementsTeamsSegmentProps) => {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const location = useLocation();

  const [achievements, setAchievements] = useState<IAchievement[]>([]);
  const [selectedAchievementIds, setSelectedAchievementIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);

  useEffect(() => {
    if (!bandId) {
      setAchievements([]);
      setSelectedAchievementIds([]);
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    const fetchAchievements = async () => {
      setLoading(true);
      try {
        const achievementsResponse = await axiosPrivate.get('/achievements', {
          signal: controller.signal,
        });

        let linkedIds: number[] = [];
        try {
          const linksResponse = await axiosPrivate.get(`/achievements/bands/${bandId}`, {
            signal: controller.signal,
          });
          linkedIds = (linksResponse.data as IAchievementLink[]).map((item) => item.idOsiagniecia);
        } catch (error) {
          if (isAxiosError(error) && error.code === 'ERR_CANCELED') {
            return;
          }

          // If backend route is not available yet, still render achievements list.
          if (!(isAxiosError(error) && error.response?.status === 404)) {
            throw error;
          }
        }

        if (isMounted) {
          setAchievements(achievementsResponse.data as IAchievement[]);
          setSelectedAchievementIds(linkedIds);
        }
      } catch (error) {
        if (isAxiosError(error) && error.code === 'ERR_CANCELED') {
          return;
        }

        if (isAxiosError(error)) {
          if (error.response?.status === 403) {
            navigate('/', { state: { from: location }, replace: true });
          } else {
            console.error('Error fetching achievements:', error);
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchAchievements();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [axiosPrivate, bandId, navigate, location]);

  const handleToggleAchievement = async (achievementId: number, checked: boolean) => {
    if (!bandId) return;

    setSavingId(achievementId);

    try {
      if (checked) {
        await axiosPrivate.post(`/achievements/bands/${bandId}`, { achievementId });
        setSelectedAchievementIds((prev) =>
          prev.includes(achievementId) ? prev : [...prev, achievementId]
        );
      } else {
        await axiosPrivate.delete(`/achievements/bands/${bandId}/${achievementId}`);
        setSelectedAchievementIds((prev) => prev.filter((id) => id !== achievementId));
      }
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 403) {
        navigate('/', { state: { from: location }, replace: true });
      } else {
        console.error('Error updating band achievement relation:', error);
      }
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="achievements-segment">
      {!bandId ? (
        <Typography color="text.secondary">Wybierz zespół, aby przypisać osiągnięcia.</Typography>
      ) : null}

      <TableContainer component={Paper}
      sx={{minHeight:600,maxHeight:600}}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Przypisane</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Nazwa</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  Ładowanie...
                </TableCell>
              </TableRow>
            ) : achievements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  Brak osiągnięć
                </TableCell>
              </TableRow>
            ) : (
              achievements.map((achievement) => (
                <TableRow key={achievement.idOsiagniecia}>
                  <TableCell>
                    <Checkbox
                      checked={selectedAchievementIds.includes(achievement.idOsiagniecia)}
                      disabled={!bandId || savingId === achievement.idOsiagniecia}
                      onChange={(event) =>
                        handleToggleAchievement(achievement.idOsiagniecia, event.target.checked)
                      }
                    />
                  </TableCell>
                  <TableCell>{achievement.idOsiagniecia}</TableCell>
                  <TableCell>{achievement.nazwa}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default AchievementsTeamsSegment;
